import { useCallback, useEffect, useState } from 'react';
import { useParams as useParamsBase } from 'react-router-dom';

import { logger as loggerBase } from '../../../datadog';
import { EventTypeEnum } from '../../../segment/types';
import { shouldShowPiiAuthFlowGate } from '../../../statsig/gates';
import {
  DEFAULT_DEPENDENCIES as DEFAULT_USE_INTRO_ROOM_DATA_DEPENDENCIES,
  Dependencies as UseIntroRoomDataDependencies,
  useIntroRoomData,
} from './useIntroRoomData';
import { useTrackIntroRoomEvent as useTrackIntroRoomEventBase } from './useTrackIntroRoomEvent';

export const LOG_PREFIX = '[Waiting Room Block - useIntroRoomState]';

export type IntroRoomState = 'loading' | 'default' | 'piiVerification' | 'requiredAgreementsReview';

export type Dependencies = {
  logger: typeof loggerBase;
  useParams: typeof useParamsBase;
  useTrackIntroRoomEvent: typeof useTrackIntroRoomEventBase;
} & UseIntroRoomDataDependencies;

const DEFAULT_DEPENDENCIES: Dependencies = {
  ...DEFAULT_USE_INTRO_ROOM_DATA_DEPENDENCIES,
  logger: loggerBase,
  useParams: useParamsBase,
  useTrackIntroRoomEvent: useTrackIntroRoomEventBase,
};

/**
 * Custom hook for managing the current state of the intro room.
 *
 * @param flowId - The current unique ID of the client's trial of the intro room
 * flow. This is used to track the client's progress through the flow in Segment.
 * @param dependencies - Optional overrides for the dependencies of the hook.
 * @returns The current intro room state and a callback to progress the state
 * when PII verification is successful.
 */
export function useIntroRoomState(flowId: string, dependencies: Dependencies = DEFAULT_DEPENDENCIES) {
  const { logger, useAuth0, useFeatureGate, useParams, useTrackIntroRoomEvent } = dependencies;
  const [introRoomState, setIntroRoomState] = useState<IntroRoomState>('loading');

  const { patientShortId } = useParams();
  const { isAuthenticated } = useAuth0();
  const { userViewed } = useTrackIntroRoomEvent();

  const { value: isPiiAuthFlowGateEnabled } = useFeatureGate(shouldShowPiiAuthFlowGate);

  const { isLoading, isMissingData, requiresWaitingRoomBlock, isEvernorthPatient } = useIntroRoomData(
    { flowId, shouldRetryFailedQueries: true },
    dependencies,
  );

  useEffect(
    function setStateBasedOnMissingConsents() {
      if (isLoading) return;

      if (!requiresWaitingRoomBlock) {
        if (isMissingData) {
          logger.warn(`${LOG_PREFIX} Missing data for client, showing default intro room`, {
            flowId,
            patientShortId,
            isAuthenticated,
          });
        } else {
          logger.info(`${LOG_PREFIX} Client does NOT require waiting room block, showing default intro room`, {
            flowId,
            patientShortId,
            isAuthenticated,
          });
        }

        userViewed(EventTypeEnum.CLIENT_NEW_INTRO_ROOM_VIEWED, {
          flowId,
          // Indicate if we were unable to verify that the client had all
          // required consents on file.
          isVerified: !isMissingData,
        });
        return setIntroRoomState('default');
      }

      if (isAuthenticated) {
        logger.info(
          `${LOG_PREFIX} Authenticated client requires waiting room block, showing required agreements review`,
          { flowId, patientShortId },
        );

        userViewed(EventTypeEnum.CLIENT_NEW_INTRO_ROOM_REQUIRED_AGREEMENTS_REVIEW_VIEWED, {
          flowId,
          isClientUserAuthenticated: true,
        });
        return setIntroRoomState('requiredAgreementsReview');
      }

      // Unauthenticated clients who are missing the required legal agreements
      // are presented with an alternative flow to verify their identity by
      // supplying PII, rather than needing to log in, since some clients have
      // never activated their accounts or may not remember their
      // email/password. However, clients still fail the verification to a
      // non-trivial degree. Temporarily, to reduce the risk of blocking care,
      // we prioritize collecting consents only for Evernorth clients. For
      // non-Evernorth clients (who may still be missing Grow consents), we will
      // suppress the waiting room block unless the feature gate is enabled.
      const shouldShowPiiVerification = isEvernorthPatient || isPiiAuthFlowGateEnabled;
      if (shouldShowPiiVerification) {
        logger.info(`${LOG_PREFIX} Client requires waiting room block, showing PII verification flow`, {
          flowId,
          patientShortId,
          isEvernorthPatient,
          isPiiAuthFlowGateEnabled,
        });
        userViewed(EventTypeEnum.CLIENT_NEW_INTRO_ROOM_PII_VERIFICATION_VIEWED, { flowId });
        return setIntroRoomState('piiVerification');
      }

      logger.info(
        `${LOG_PREFIX} Non-Evernorth client requires waiting room block, but it is suppressed (feature gate is disabled), showing default intro room`,
        { flowId, patientShortId, isPiiAuthFlowGateEnabled },
      );
      userViewed(EventTypeEnum.CLIENT_NEW_INTRO_ROOM_VIEWED, {
        flowId,
        isVerified: !isMissingData,
      });
      return setIntroRoomState('default');
    },
    [
      logger,
      flowId,
      isAuthenticated,
      isLoading,
      isEvernorthPatient,
      isMissingData,
      isPiiAuthFlowGateEnabled,
      patientShortId,
      requiresWaitingRoomBlock,
      userViewed,
    ],
  );

  const onPIIVerified = useCallback(() => {
    userViewed(EventTypeEnum.CLIENT_NEW_INTRO_ROOM_REQUIRED_AGREEMENTS_REVIEW_VIEWED, {
      flowId,
      isClientUserAuthenticated: false,
    });
    setIntroRoomState('requiredAgreementsReview');
  }, [flowId, userViewed]);

  return {
    introRoomState,
    onPIIVerified,
  };
}
